import { React, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  List, Space, Tooltip,
} from 'antd';
import { CheckCircleTwoTone, CloseCircleTwoTone, InfoCircleOutlined } from '@ant-design/icons';
import { calcProjectCoords, calcUserDistances } from './scheduleHelpers';
import { isNullOrUndefined } from '../helpers/helpers';

import OntraccrTag from '../common/OntraccrTag';

const tagColor = (status) => {
  switch (status) {
    case 'accepted': return 'green';
    case 'rejected': return 'red';
    case 'withdrawn': return 'orange';
    default: return 'blue';
  }
};

const getTagStyle = (status) => (
  status === 'accepted' ? { border: '1px solid #52c41a' } : {}
);

export default function ScheduleApplicantSelector({
  entireUserMap,
  users,
  appliedUsers,
  onUpdateStatus,
  hasWritePerm,
  project,
}) {
  const projectCoords = useMemo(
    () => calcProjectCoords(project),
    [project],
  );
  const userDistances = useMemo(
    () => calcUserDistances(projectCoords, users),
    [projectCoords, users],
  );
  const userMap = users.reduce((acc, user) => {
    acc[user.id] = user.name;
    return acc;
  }, {});


  return (
    <List
      bordered
      dataSource={appliedUsers}
      renderItem={({ userId, status }) => {
        const userFromEntireMap = entireUserMap[userId];

        const name = userMap[userId]
        ?? userFromEntireMap?.name
        ?? 'Unknown User';

        const notInTeam = !userMap[userId];
        const distance = userDistances[userId];
        return (
          <List.Item style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space align="center">
              {!isNullOrUndefined(distance) && (
                <OntraccrTag color="blue" style={{ minWidth: 60, textAlign: 'center' }}>
                  {distance.toFixed(1)} km
                </OntraccrTag>
              )}
              <span style={{ fontSize: 14, fontWeight: 500 }}>{name}</span>
            </Space>
            {notInTeam ? (
              <Tooltip
                title="This user is not part of the selected division or team. To accept or reject them, either change the division or remove the team filter, or add the user to the division or team."
                placement="left"
              >
                <InfoCircleOutlined style={{ fontSize: '20px', color: 'orange' }} />
              </Tooltip>
            ) : (
              <Space>
                <OntraccrTag
                  color={tagColor(status)}
                  style={getTagStyle(status)}
                >
                  {status}
                </OntraccrTag>
                <CheckCircleTwoTone
                  twoToneColor={status === 'accepted' ? 'green' : '#ccc'}
                  style={{
                    fontSize: '24px',
                    cursor: 'pointer',
                  }}
                  onClick={hasWritePerm && !notInTeam ? () => onUpdateStatus(userId, 'accepted') : null}
                />
                <CloseCircleTwoTone
                  twoToneColor={status === 'rejected' ? 'red' : '#ccc'}
                  style={{
                    fontSize: '24px',
                    cursor: 'pointer',
                  }}
                  onClick={hasWritePerm && !notInTeam ? () => onUpdateStatus(userId, 'rejected') : null}
                />
              </Space>
            )}
          </List.Item>
        );
      }}
    />
  );
}

ScheduleApplicantSelector.propTypes = {
  appliedUsers: PropTypes.arrayOf(
    PropTypes.shape({
      userId: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
    }),
  ).isRequired,
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onUpdateStatus: PropTypes.func.isRequired, // Function to handle status updates
  hasWritePerm: PropTypes.bool.isRequired,
  entireUserMap: PropTypes.objectOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  project: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    address: PropTypes.string,
  }),
};
